/**
 * NWF-168: keeps the `_nwf_buyable` cart attribute in sync with what the customer can actually
 * buy, so the Staffelpreis is pooled over purchasable units only.
 *
 * Why an attribute at all: the Cart Transform Function decides the tier, and nothing in its
 * schema exposes availability — `ProductVariant` there has no inventory field, and `/cart.js`
 * carries none per item. Only Liquid sees it. nwf-cart-staffel.liquid publishes the numbers,
 * this script writes them onto the cart, the Functions read them back.
 *
 * Why the CART and not the line (which is what NWF-168 shipped with first): a line property can
 * only be written with `POST /cart/change.js`, and that endpoint re-adds the line and re-validates
 * its stock. On exactly the lines this feature exists for — the sold-out ones — Shopify refuses
 * with HTTP 422 ("Das Produkt … ist bereits ausverkauft."), so the write never landed and the sync
 * retried on every page load; and on a line it did accept, omitting `quantity` silently reset that
 * line to 1. `POST /cart/update.js` with `attributes` touches no line, is accepted while sold-out
 * lines sit in the cart, and needs one request for the whole cart instead of one per line.
 *
 * The map holds `variantId: remaining` for the limited variants only. A missing variant counts in
 * full everywhere that reads the map, which is the safe direction: absent cannot be told apart from
 * "nothing has looked yet", and dropping quantity on a guess would take a tier away from a shopper
 * who earned it.
 */
(function () {
    'use strict';

    /* Both cart sections render the snippet that loads this file, so on a page carrying the
       cart and the drawer the tag appears twice and each one executes. Without the sentinel
       the second run would add a second cartUpdate subscription and double every sync.
       Same guard as nwf-meterware.js. */
    if (window.__nwfCartStaffelInit) return;
    window.__nwfCartStaffelInit = true;

    var ATTRIBUTE = '_nwf_buyable';
    var COUNTRY_ATTRIBUTE = '_nwf_country';
    /* One write settles the whole cart, so the cap only stops a pathological ping-pong — a value
       the server stores differently from how Liquid keeps asking for it. */
    var MAX_PASSES = 3;
    var passes = 0;
    var running = false;

    function payloadNode() {
        return document.querySelector('[data-nwf-staffel-stock]');
    }

    /**
     * The value the attribute should hold, in ONE canonical spelling — keys sorted, no spaces —
     * so it can be compared to the stored string without parsing it back. An empty map serialises
     * to the empty string, which is how the cart API deletes an attribute.
     */
    function wantedValue(node) {
        var stock;
        try {
            stock = JSON.parse(node.textContent);
        } catch (error) {
            /* A broken payload must never touch the cart — leave the attribute as it is. */
            return null;
        }
        if (!stock || typeof stock !== 'object' || Array.isArray(stock)) return null;

        var keys = Object.keys(stock).sort();
        if (keys.length === 0) return '';

        var canonical = {};
        for (var index = 0; index < keys.length; index += 1) {
            var remaining = parseInt(stock[keys[index]], 10);
            if (Number.isNaN(remaining) || remaining < 0) return null;
            canonical[keys[index]] = remaining;
        }
        return JSON.stringify(canonical);
    }

    /* What the cart holds right now. Liquid rendered it into the same node, so a cart that is
       already in sync costs no request at all — and after our own write the sections re-render,
       which is what makes the next pass see the new value and stop. */
    function currentValue(node) {
        return node.dataset.nwfStaffelCurrent || '';
    }

    function collectSections() {
        var sections = [];
        document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } }),
        );
        return sections;
    }

    /**
     * Writes the attribute and returns the cart the change produced, or null. `sections` comes
     * along so the response carries the re-rendered cart markup: publishing a cart without
     * `sections` makes the theme's renderer bail out, and the totals the Function just changed
     * would stay stale until the next page load.
     */
    function write(value, node) {
        var body = JSON.stringify({
            attributes: attributePayload(value, node),
            sections: collectSections(),
            sections_url: window.location.pathname,
        });
        return fetch(FoxTheme.routes.cart_update_url, Object.assign({}, FoxTheme.utils.fetchConfig(), { body: body }))
            .then(function (response) {
                return response.json();
            })
            .catch(function () {
                /* A failed write leaves every line counting in full — see the module comment. */
                return null;
            });
    }

    function wantedCountry(node) {
        return (node.dataset.nwfCountry || '').toUpperCase() || null;
    }

    function currentCountry(node) {
        return node.dataset.nwfCountryCurrent || '';
    }

    function attributePayload(value, node) {
        var attributes = {};
        attributes[ATTRIBUTE] = value;
        var country = wantedCountry(node);
        if (country !== null) {
            attributes[COUNTRY_ATTRIBUTE] = country;
        }
        return attributes;
    }

    function sync() {
        if (running || passes >= MAX_PASSES) return;
        if (typeof FoxTheme === 'undefined' || !FoxTheme.routes) return;

        var node = payloadNode();
        if (!node) return;

        var wanted = wantedValue(node);
        if (wanted === null) return;
        var stockSettled = wanted === currentValue(node);
        var country = wantedCountry(node);
        var countrySettled = country === null || country === currentCountry(node);
        if (stockSettled && countrySettled) {
            /* The cap counts CONSECUTIVE writing passes, and a settled pass clears it. Counting
               every pass would switch the sync off after three quantity edits in one session,
               which is normal use, not a loop. */
            passes = 0;
            return;
        }

        running = true;

        write(wanted, node)
            .then(function (cart) {
                /* Counted whatever came back: a write the server keeps refusing must stop after
                   MAX_PASSES too, or the sync would try again on every cart event. */
                passes += 1;

                /* An error response (422, 429, …) is JSON too, but it is not a cart. Publishing it
                   would hand the theme's cart subscribers an object with no `items` and no
                   `item_count` — and cart.js reads `cart.errors` off whatever arrives, alerts it
                   and navigates to the cart page, which is the loop NWF-168 shipped with. */
                if (!cart || !Array.isArray(cart.items)) {
                    if (window.console) {
                        console.warn('[nwf-cart-staffel] cart attribute not written', cart);
                    }
                    return;
                }

                FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
            })
            .finally(function () {
                running = false;
            });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', sync);
    } else {
        sync();
    }

    /* The cart sections re-render on every change, which replaces the payload above with fresh
       numbers and the fresh stored value. Re-reading it then is what keeps the attribute current
       after a quantity edit or a removal. */
    document.addEventListener('DOMContentLoaded', function () {
        if (typeof FoxTheme === 'undefined' || !FoxTheme.pubsub) return;
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function () {
            /* Let the section markup land before reading the payload it carries. */
            window.setTimeout(sync, 0);
        });
    });
})();
